import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import Navbar from './Navbar';
import { checkAndUpdateEmployeeData } from './employeeUpdates';
import { FaUser, FaEnvelope, FaCalendarAlt, FaClock, FaSitemap, FaUserTag, FaCalendarCheck, FaHourglass, FaExchangeAlt, FaBirthdayCake, FaGavel } from 'react-icons/fa';
import { motion } from 'framer-motion';
import './EmployeeInfo.css';
import { calculateDaysTaken } from '../utils/daysTakenCalculation';
import HelpModal from './HelpModal';

function EmployeeInfo() {
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [overtimeData, setOvertimeData] = useState(null);
    const [userRole, setUserRole] = useState('');
    const [daysTaken, setDaysTaken] = useState(0);
    const [helpOpen, setHelpOpen] = useState(false); // For HelpModal

    useEffect(() => {
        fetchEmployeeInfo();
    }, []);

    const fetchEmployeeInfo = async () => {
        try {
            setLoading(true);

            // Get the current authenticated user
            const { data: { user } } = await supabase.auth.getUser();

            if (!user) {
                throw new Error("No se encontró usuario autenticado");
            }

            // Fetch user role
            const { data: userData, error: userError } = await supabase
                .from('users')
                .select('role')
                .eq('wsp_email', user.email)
                .single();

            if (userError) throw userError;
            setUserRole(userData.role);

            // Fetch employee data
            const { data: employeeData, error: employeeError } = await supabase
                .from('employees')
                .select('*')
                .eq('wsp_email', user.email)
                .single();

            if (employeeError) throw employeeError;

            // Update employee data with the algorithm
            const result = await checkAndUpdateEmployeeData([employeeData]);

            if (result.success && result.updatedEmployees && result.updatedEmployees.length > 0) {
                const updatedEmployee = result.updatedEmployees[0];
                setEmployee(updatedEmployee);

                // Calculate days taken
                const calculatedDaysTaken = await calculateDaysTaken(
                    updatedEmployee.renovation_day,
                    updatedEmployee.id
                );
                setDaysTaken(calculatedDaysTaken);

                // Fetch overtime data
                const { data: overtimeData, error: overtimeError } = await supabase
                    .from('overtime_vacation_days')
                    .select('*')
                    .eq('employee_id', updatedEmployee.id)
                    .single();

                if (overtimeError && overtimeError.code !== 'PGRST116') throw overtimeError;
                setOvertimeData(overtimeData);
            } else {
                throw new Error("Error al actualizar datos del empleado");
            }

        } catch (error) {
            console.error("Error fetching employee info:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Calculate employee's seniority in years
    const calculateSeniority = (dateOfEntry) => {
        if (!dateOfEntry) return 0;

        const today = new Date();
        const entryDate = new Date(dateOfEntry);

        let years = today.getFullYear() - entryDate.getFullYear();
        const months = today.getMonth() - entryDate.getMonth();

        if (months < 0 || (months === 0 && today.getDate() < entryDate.getDate())) {
            years--;
        }

        return years;
    };

    // Format a date string to Spanish format
    const formatDate = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);
        date.setDate(date.getDate() + 1); // Adjust for timezone

        const options = {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };

        return date.toLocaleDateString('es-MX', options);
    };

    // Add the calculateVacationDays function
    const calculateVacationDays = (dateOfEntry) => {
        if (!dateOfEntry) return 0;
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const todayDate = new Date(todayStr);

        const entryStr = dateOfEntry.split('T')[0];
        const entryDate = new Date(entryStr);

        let years = todayDate.getFullYear() - entryDate.getFullYear();

        const anniversaryDate = new Date(
            todayDate.getFullYear(),
            entryDate.getMonth(),
            entryDate.getDate()
        );

        if (todayDate < anniversaryDate) {
            years--;
        }

        if (years <= 0) return 0;
        else if (years === 1) return 12;
        else if (years === 2) return 14;
        else if (years === 3) return 16;
        else if (years === 4) return 18;
        else if (years === 5) return 20;
        else if (years >= 6 && years <= 10) return 22;
        else if (years >= 11 && years <= 15) return 24;
        else if (years >= 16 && years <= 20) return 26;
        else if (years >= 21 && years <= 25) return 28;
        else if (years >= 26 && years <= 30) return 30;
        else return 32;
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                when: "beforeChildren",
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.5 }
        }
    };

    if (loading) {
        return (
            <div className="employee-info-page">
                <Navbar />
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Cargando información...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="employee-info-page">
                <Navbar />
                <div className="error-container">
                    <div className="error-icon">!</div>
                    <h2>Error al cargar la información</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="employee-info-page">
            <Navbar />

            <div className="employee-info-content">
                <motion.div
                    className="employee-info-header"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <h1>Hola {employee?.full_name}, esta es tu información al día de hoy.</h1>
                </motion.div>

                <motion.div
                    className="employee-info-card"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <motion.div className="info-item" variants={itemVariants}>
                        <FaEnvelope className="info-icon" />
                        <div className="info-content">
                            <h3>Correo WSP</h3>
                            <p>{employee?.wsp_email}</p>
                        </div>
                    </motion.div>

                    <motion.div className="info-item" variants={itemVariants}>
                        <FaCalendarAlt className="info-icon" />
                        <div className="info-content">
                            <h3>Fecha de Ingreso</h3>
                            <p>{formatDate(employee?.date_of_entry)}</p>
                        </div>
                    </motion.div>

                    <motion.div className="info-item" variants={itemVariants}>
                        <FaClock className="info-icon" />
                        <div className="info-content">
                            <h3>Antigüedad</h3>
                            <p>{calculateSeniority(employee?.date_of_entry)} años en la empresa</p>
                        </div>
                    </motion.div>

                    <motion.div className="info-item" variants={itemVariants}>
                        <FaGavel className="info-icon" />
                        <div className="info-content">
                            <h3>Días que te corresponden por ley</h3>
                            <p>{calculateVacationDays(employee?.date_of_entry)} días</p>
                        </div>
                    </motion.div>

                    <motion.div className="info-item" variants={itemVariants}>
                        <FaSitemap className="info-icon" />
                        <div className="info-content">
                            <h3>Área</h3>
                            <p>{employee?.area || 'No especificada'}</p>
                        </div>
                    </motion.div>

                    <motion.div className="info-item" variants={itemVariants}>
                        <FaUserTag className="info-icon" />
                        <div className="info-content">
                            <h3>Rol en el Portal</h3>
                            <p>{userRole}</p>
                        </div>
                    </motion.div>

                    <motion.div className="info-item" variants={itemVariants}>
                        <FaCalendarCheck className="info-icon" />
                        <div className="info-content">
                            <h3>Días Disponibles</h3>
                            <p className="highlight">{employee?.remaining_days} días</p>
                        </div>
                    </motion.div>

                    {overtimeData && overtimeData.days_remaining !== null ? (
                        <motion.div className="info-item" variants={itemVariants}>
                            <FaHourglass className="info-icon" />
                            <div className="info-content">
                                <h3>Días de Prórroga</h3>
                                <p>De tus {employee?.remaining_days} días disponibles, <span className="highlight">{overtimeData.days_remaining}</span> días son de prórroga y vencen el <span className="highlight">{formatDate(overtimeData.expiration_date)}</span>.</p>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div className="info-item" variants={itemVariants}>
                            <FaHourglass className="info-icon" />
                            <div className="info-content">
                                <h3>Días de Prórroga</h3>
                                <p>Todos tus días pertenecen a tu período actual de vacaciones. No tienes días de prórroga.</p>
                            </div>
                        </motion.div>
                    )}

                    <motion.div className="info-item" variants={itemVariants}>
                        <FaExchangeAlt className="info-icon" />
                        <div className="info-content">
                            <h3>Días Tomados (Ciclo Actual)</h3>
                            <p>{daysTaken} días</p>
                        </div>
                    </motion.div>

                    <motion.div className="info-item" variants={itemVariants}>
                        <FaBirthdayCake className="info-icon" />
                        <div className="info-content">
                            <h3>Próxima Renovación de tus vacaciones</h3>
                            <p>Tu siguiente aniversario en WSP International es el<br /><span className="highlight">{formatDate(employee?.renovation_day)}</span></p>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Stylish error report button */}
                <div className="employee-info-help-btn-container">
                  <button
                    className="employee-info-help-btn"
                    onClick={() => setHelpOpen(true)}
                  >
                    ¿Hay un dato erróneo?
                  </button>
                </div>
                <HelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
            </div>
        </div>
    );
}

export default EmployeeInfo;